import { useEffect, useState } from 'react';
import CheckboxesGroup from 'src/components/CheckboxesGroup';
import Collapsible from 'src/components/Collapsible';
import { Sidebar, SidebarContainer, SidebarContent } from 'src/components/Sidebar';
import { countAllFilters } from 'src/filters/countAllFilters';
import FilterByDateRange from 'src/filters/FilterByDateRange';
import FiltersSidebarFooter from 'src/filters/FiltersSidebarFooter';
import FiltersSidebarHeader from 'src/filters/FiltersSidebarHeader';
import FiltersTriggerButton from 'src/filters/FiltersTriggerButton';
import { useAppDispatch, useAppSelector } from 'src/store';

import { DocumentType } from './singleStudent.types';
import {
  selectStudentDocumentsFilters,
  selectStudentDocumentsFiltersCount,
} from './StudentSelectors';
import { setStudentDocumentsFilters } from './StudentSlice';

type StudentDocumentsFiltersProps = {
  studentYearFilterId?: string;
  forceOpen?: boolean;
  onFiltersClosed?: () => void;
};

const StudentDocumentsFilters = ({
  studentYearFilterId,
  forceOpen,
  onFiltersClosed,
}: StudentDocumentsFiltersProps) => {
  const dispatch = useAppDispatch();
  const initialFilters = useAppSelector(selectStudentDocumentsFilters);
  const initialFiltersCount = useAppSelector(selectStudentDocumentsFiltersCount);

  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>();
  const [from, setFrom] = useState<string>();
  const [to, setTo] = useState<string>();

  const [open, setOpen] = useState(forceOpen ?? false);

  const filters = {
    documentTypes,
    from,
    to,
    schoolYearId: studentYearFilterId,
  };

  useEffect(() => {
    setDocumentTypes(initialFilters.documentTypes);
    setFrom(initialFilters.from);
    setTo(initialFilters.to);
  }, [initialFilters, open]);

  const onApply = () => {
    dispatch(setStudentDocumentsFilters(filters));
  };

  const onReset = () => {
    setDocumentTypes(undefined);
    setFrom(undefined);
    setTo(undefined);
  };

  const filtersCount = countAllFilters(filters);

  if (forceOpen && !open) {
    setOpen(true);
  }

  const documentTypeOptions = [
    { label: 'Service Coordination', value: 'ServiceCoordination' as DocumentType },
    { label: 'Attendance Log', value: 'Attendance' as DocumentType },
    { label: 'Timesheet', value: 'Timesheet' as DocumentType },
    { label: 'Coordination Log', value: 'CoordinationLog' as DocumentType },
    { label: 'Invoice', value: 'Invoice' as DocumentType },
    { label: 'Session Notes', value: 'SessionNotes' as DocumentType },
  ];

  return (
    <SidebarContainer
      onOpenChange={(val) => {
        !val && onFiltersClosed && onFiltersClosed();
        setOpen(val);
      }}
      open={open}
    >
      <FiltersTriggerButton
        initialFiltersCount={initialFiltersCount}
        iconClassName="text-neutral-500"
      />
      <Sidebar open={open}>
        <FiltersSidebarHeader
          initialFiltersCount={initialFiltersCount}
          onReset={onReset}
          filtersCount={filtersCount}
        />
        <SidebarContent className="p-4 md:p-6">
          <Collapsible title="Document type" separator={true} defaultOpen={true}>
            <div className="grid grid-cols-1 gap-y-2">
              <CheckboxesGroup
                value={documentTypes}
                onChange={setDocumentTypes}
                options={documentTypeOptions}
              />
            </div>
          </Collapsible>
          <FilterByDateRange
            startDate={from}
            endDate={to}
            setStartDate={setFrom}
            setEndDate={setTo}
            separator={false}
            defaultOpen={true}
          />
        </SidebarContent>
        <FiltersSidebarFooter onApply={onApply} filtersCount={filtersCount} />
      </Sidebar>
    </SidebarContainer>
  );
};

export default StudentDocumentsFilters;
